import { Megaphone } from 'lucide-react';
/**
 * Sub-spec 10 Plan 4 — staff marketing campaigns page.
 *
 * Two-column layout: campaigns list on the left, editor on the right.
 * Status grouping: drafts → scheduled → sent → cancelled/failed.
 */
import { useCallback, useEffect, useState } from 'react';
import { CampaignAnalytics } from './CampaignAnalytics';
import { FilterPillBuilder } from './FilterPillBuilder';
import { JourneyBuilder } from './JourneyBuilder';
import { NlSegmentBuilder } from './NlSegmentBuilder';
import type {
  CampaignStatus,
  MarketingCampaign,
  MarketingChannel,
  PredicateRegistry,
  SegmentFilter,
} from './types';

const STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: 'Drafts',
  scheduled: 'Scheduled',
  sending: 'Sending',
  sent: 'Sent',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

const STATUS_ORDER: CampaignStatus[] = [
  'draft',
  'scheduled',
  'sending',
  'sent',
  'cancelled',
  'failed',
];

function emptyEditorState(): Partial<MarketingCampaign> {
  return {
    name: '',
    channelSubset: ['email'],
    templateSubject: '',
    templateBody: '',
    segmentFilter: { predicates: [] },
    scheduledFor: null,
  };
}

export function CampaignsPage() {
  const [campaigns, setCampaigns] = useState<MarketingCampaign[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editor, setEditor] = useState<Partial<MarketingCampaign>>(emptyEditorState());
  const [registry, setRegistry] = useState<PredicateRegistry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingState, setSavingState] = useState<'idle' | 'saving'>('idle');
  const [preview, setPreview] = useState<{ count: number; sample: string[] } | null>(null);

  const loadCampaigns = useCallback(async () => {
    try {
      const res = await fetch('/api/marketing/campaigns', { credentials: 'include' });
      if (res.status === 403) {
        setError('You need the marketing.configure capability to manage campaigns.');
        setCampaigns([]);
        return;
      }
      if (!res.ok) {
        setError(`Failed to load campaigns (${res.status}).`);
        return;
      }
      const body = (await res.json()) as { campaigns: MarketingCampaign[] };
      setCampaigns(body.campaigns);
    } catch {
      setError('Network error.');
      setCampaigns([]);
    }
  }, []);

  const loadRegistry = useCallback(async () => {
    try {
      const res = await fetch('/api/marketing/segment-predicates', { credentials: 'include' });
      if (!res.ok) return;
      const body = (await res.json()) as { predicates: PredicateRegistry };
      setRegistry(body.predicates);
    } catch {
      // Non-fatal; the filter UI will show a placeholder.
    }
  }, []);

  useEffect(() => {
    void loadCampaigns();
    void loadRegistry();
  }, [loadCampaigns, loadRegistry]);

  function selectCampaign(c: MarketingCampaign | null) {
    setSelectedId(c?.id ?? null);
    setPreview(null);
    if (c) {
      setEditor({
        ...c,
        // Drizzle returns segmentFilter parsed already (from route layer).
        segmentFilter: c.segmentFilter,
      });
    } else {
      setEditor(emptyEditorState());
    }
  }

  async function saveDraft() {
    setSavingState('saving');
    setError(null);
    try {
      const body = {
        name: editor.name,
        channelSubset: editor.channelSubset,
        templateSubject: editor.templateSubject,
        templateBody: editor.templateBody,
        segmentFilter: editor.segmentFilter,
        scheduledFor: editor.scheduledFor ?? null,
      };
      const url = selectedId
        ? `/api/marketing/campaigns/${selectedId}`
        : '/api/marketing/campaigns';
      const method = selectedId ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as { issues?: string[] };
        setError(errBody.issues?.join(', ') ?? `Save failed (${res.status})`);
        return;
      }
      const savedBody = (await res.json()) as { campaign: MarketingCampaign };
      await loadCampaigns();
      selectCampaign(savedBody.campaign);
    } finally {
      setSavingState('idle');
    }
  }

  async function cancelCampaign() {
    if (!selectedId) return;
    if (!confirm('Cancel this campaign? Recipients will not be contacted.')) return;
    const res = await fetch(`/api/marketing/campaigns/${selectedId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (res.ok) {
      await loadCampaigns();
      selectCampaign(null);
    }
  }

  async function loadPreview() {
    if (!selectedId) return;
    const res = await fetch(`/api/marketing/campaigns/${selectedId}/preview-segment`, {
      credentials: 'include',
    });
    if (res.ok) {
      const body = (await res.json()) as {
        recipientCount: number;
        sample: Array<{ name: string }>;
      };
      setPreview({ count: body.recipientCount, sample: body.sample.map((s) => s.name) });
    }
  }

  function toggleChannel(ch: MarketingChannel) {
    const current = new Set(editor.channelSubset ?? []);
    if (current.has(ch)) current.delete(ch);
    else current.add(ch);
    setEditor({ ...editor, channelSubset: Array.from(current) });
  }

  function onSegmentChange(next: SegmentFilter) {
    setEditor({ ...editor, segmentFilter: next });
  }

  if (campaigns === null) return <div className="p-6 text-sm">Loading…</div>;

  return (
    <div className="grid min-h-[100dvh] grid-cols-1 md:grid-cols-[300px_1fr]">
      {/* Left list */}
      <aside className="flex flex-col border-r border-border bg-background">
        {/* Sticky header */}
        <div className="sticky top-0 z-10 border-b border-border bg-background px-4 py-3 flex items-center gap-2">
          <Megaphone className="size-4 shrink-0 text-muted-foreground" />
          <h1 className="text-sm font-semibold flex-1 min-w-0">Campaigns</h1>
        </div>
        {/* Scrollable list */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          <button
            type="button"
            onClick={() => selectCampaign(null)}
            className="w-full rounded-md border px-3 py-2 text-sm hover:bg-secondary text-left"
          >
            + New campaign
          </button>
          {STATUS_ORDER.map((status) => {
            const inGroup = campaigns.filter((c) => c.status === status);
            if (inGroup.length === 0) return null;
            return (
              <div key={status} className="space-y-1">
                <div className="px-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {STATUS_LABEL[status]}
                </div>
                {inGroup.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => selectCampaign(c)}
                    className={`w-full text-left rounded-md px-3 py-2 text-sm ${
                      selectedId === c.id ? 'bg-secondary' : 'hover:bg-secondary/50'
                    }`}
                  >
                    <div className="font-medium truncate">
                      {c.name || <span className="text-muted-foreground italic">Untitled</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {c.recipientCount !== null
                        ? `${c.recipientCount} recipients`
                        : c.scheduledFor
                          ? `Scheduled ${new Date(c.scheduledFor).toLocaleString()}`
                          : 'Draft'}
                    </div>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </aside>

      {/* Editor */}
      <main className="overflow-y-auto p-6">
        <div className="max-w-2xl space-y-5">
          {error && (
            <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
              {error}
            </div>
          )}

          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="campaign-name">
              Name
            </label>
            <input
              id="campaign-name"
              type="text"
              value={editor.name ?? ''}
              onChange={(e) => setEditor({ ...editor, name: e.target.value })}
              className="w-full border rounded px-3 py-2 text-sm bg-background"
              placeholder="Spring promo"
            />
          </div>

          <div className="space-y-1.5">
            <div className="text-sm font-medium">Channels</div>
            <div className="flex flex-wrap gap-3 text-sm">
              {(['email', 'sms', 'whatsapp'] as MarketingChannel[]).map((ch) => (
                <label key={ch} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editor.channelSubset?.includes(ch) ?? false}
                    onChange={() => toggleChannel(ch)}
                    className="rounded border-border accent-primary"
                  />
                  <span className="capitalize">{ch}</span>
                </label>
              ))}
            </div>
          </div>

          {editor.channelSubset?.includes('email') && (
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="campaign-subject">
                Email subject
              </label>
              <input
                id="campaign-subject"
                type="text"
                value={editor.templateSubject ?? ''}
                onChange={(e) => setEditor({ ...editor, templateSubject: e.target.value })}
                className="w-full border rounded px-3 py-2 text-sm bg-background"
                placeholder="Your spring offer from…"
              />
            </div>
          )}

          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="campaign-body">
              Body
            </label>
            <textarea
              id="campaign-body"
              rows={8}
              value={editor.templateBody ?? ''}
              onChange={(e) => setEditor({ ...editor, templateBody: e.target.value })}
              className="w-full border rounded px-3 py-2 text-sm font-mono bg-background resize-y"
              placeholder="<p>Hi customers — …</p>"
            />
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Segment</div>
            <NlSegmentBuilder onApply={(f) => onSegmentChange(f)} />
            <FilterPillBuilder
              value={editor.segmentFilter ?? { predicates: [] }}
              onChange={onSegmentChange}
              registry={registry}
            />
          </div>

          {selectedId && (
            <div className="space-y-3 border-t border-border pt-4">
              <h2 className="text-sm font-semibold">Journey</h2>
              <JourneyBuilder campaignId={selectedId} />
            </div>
          )}

          {selectedId && (
            <div className="space-y-3 border-t border-border pt-4">
              <CampaignAnalytics campaignId={selectedId} />
            </div>
          )}

          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="campaign-schedule">
              Schedule for
            </label>
            <input
              id="campaign-schedule"
              type="datetime-local"
              value={
                editor.scheduledFor ? new Date(editor.scheduledFor).toISOString().slice(0, 16) : ''
              }
              onChange={(e) => {
                const v = e.target.value;
                setEditor({
                  ...editor,
                  scheduledFor: v ? new Date(v).toISOString() : null,
                });
              }}
              className="border rounded px-3 py-2 text-sm bg-background"
            />
            <div className="text-xs text-muted-foreground">
              Leave blank to keep as draft. Scheduled campaigns fire within 5 minutes of the
              scheduled time.
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={saveDraft}
              disabled={savingState === 'saving'}
              className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              {selectedId ? 'Save' : 'Create campaign'}
            </button>
            {selectedId && (
              <>
                <button
                  type="button"
                  onClick={loadPreview}
                  className="rounded-md border px-4 py-2 text-sm hover:bg-secondary"
                >
                  Preview recipients
                </button>
                <button
                  type="button"
                  onClick={cancelCampaign}
                  className="rounded-md border border-red-300 text-red-900 px-4 py-2 text-sm hover:bg-red-50"
                >
                  Cancel campaign
                </button>
              </>
            )}
          </div>

          {preview && (
            <div className="rounded border border-border bg-secondary px-3 py-2 text-sm">
              <div className="font-medium num">
                {preview.count} recipient{preview.count === 1 ? '' : 's'}
              </div>
              {preview.sample.length > 0 && (
                <div className="text-muted-foreground text-xs mt-1">
                  Sample: {preview.sample.slice(0, 5).join(', ')}
                  {preview.count > 5 ? ', …' : ''}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
